import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toHandlers as _toHandlers, mergeProps as _mergeProps, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "flex items-start gap-2 px-3 py-2 mb-4 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left",
  "data-testid": "policy-links-post-station-lock-banner"
}
const _hoisted_2 = { class: "flex flex-col md:flex-row justify-between gap-4 text-sm text-gray-600 mb-4 px-2" }
const _hoisted_3 = { class: "flex flex-col sm:flex-row items-start sm:items-center gap-4 relative" }
const _hoisted_4 = {
  key: 2,
  class: "fixed top-[calc(25vh+4rem)] right-[25vw] z-[50] bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-800" }, "Adicionar política", -1)
const _hoisted_6 = {
  key: 3,
  class: "fixed top-[calc(25vh+4rem)] right-[25vw] z-50 bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-800" }, "Editar vínculo da política", -1)
const _hoisted_8 = { class: "text-xs text-gray-500 font-medium mb-3" }
const _hoisted_9 = { class: "flex gap-2" }

import { watch, ref, computed, onMounted, toRef } from 'vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { useScheduleMaxDate } from '@/hooks/useScheduleMaxDate'

import PolicyService from '@/Network/Services/DsPaymentPolicyService'

import EditIcon from '@/assets/icons/edit.vue'
import { mdiCalendar, mdiLock, mdiPlus } from '@mdi/js'

import DsPaymentPolicySelect from '../../../Select/DsPaymentPolicySelect.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { DatePicker } from 'v-calendar'
import EmptyList from '@/components/EmptyList.vue'
import Spinner from '@/components/others/Spinner.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import { SchedulePolicyLinkDetails } from '@/Network/Types/Responses/DsPaymentPolicyResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import Legend from '@/components/designSystem/Legend/Legend.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import type { DSAttendancePolicySimpleResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import type { PaymentPolicy } from '@/Network/Types/Responses/DsPaymentPolicyResponses'
import { POLICY_DATE_MESSAGES } from '@/components/ManageScale/Schedule/schemas'
import { toDateOnly } from '@/utils/date'

interface DraftItem {
  id?: number
  policyId: number
  policyName: string
  startDate: string
  endDate: string | null
  linkedAt: string
  linkedBy: string
  status: 'vinculada' | 'em_edicao' | 'nao_vinculada'
}

interface PolicyLinkPayload {
  id?: number
  policyId: number
  startDate: Date
  endDate: Date | null
  isDeleted: boolean
}
type LinkPoliciesToScheduleRequestData = PolicyLinkPayload[]


export default /*#__PURE__*/_defineComponent({
  __name: 'PolicyLinksTab',
  props: {
    scheduleId: {},
    isSaving: { type: Boolean },
    scheduleEndDate: {}
  },
  emits: ["update:isSaving", "update:hasUnsavedChanges"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const { flags } = useFeatureFlags()

const attendancePolicies = ref<DSAttendancePolicySimpleResponse[]>([])
const isLoadingAttendancePolicies = ref(false)

// `toast` permanece para as validações locais (datas, campos obrigatórios) —
// erros vindos da API passam pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()
const permissionsStore = usePermissionsStore()

const policyService = new PolicyService()

const draftItems = ref<DraftItem[]>([])
const deletedItems = ref<DraftItem[]>([])
const showAddPolicyForm = ref(false)

const selectedPolicy = ref<PaymentPolicy | null>(null)
const startDate = ref<string | Date>('')
const endDate = ref<string | Date | null>('')
const showEditPolicyForm = ref(false)
const editItemIndex = ref<number | null>(null)
const editPolicy = ref<DraftItem | null>(null)
const originalItems = ref<DraftItem[]>([])
const isLoadingPolicies = ref(false)
const isSavingPolicies = ref(false)
const isEndDateIndeterminate = ref(false)
const isEditEndDateIndeterminate = ref(false)

const { scheduleMaxDate } = useScheduleMaxDate(toRef(props, 'scheduleEndDate'))

const canCreatePolicyLink = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.SchedulePaymentPoliciesCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canDeactivatePolicyLink = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.SchedulePaymentPoliciesDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canEditPolicyLinks = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.SchedulePolicyLinksEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const fetchAttendancePolicies = async () => {
  // Tem que retornar a listagem de containers e nao das políticas
  // isLoadingAttendancePolicies.value = true
  // try {
  //   const data = await Network.dsAttendance.listSimple()
  //   attendancePolicies.value = data
  // } catch (error: unknown) {
  //   toast.error('Erro ao carregar lista simplificada de políticas.')
  // } finally {
  //   isLoadingAttendancePolicies.value = false
  // }
}

watch(
  () => flags.enabledDsAttendancePolicy,
  (isEnabled) => {
    if (isEnabled && attendancePolicies.value.length === 0) {
      fetchAttendancePolicies()
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await fetchLinkedPolicies()
})

watch(isEndDateIndeterminate, (isIndeterminate) => {
  if (isIndeterminate) {
    endDate.value = null
  }
})

watch(isEditEndDateIndeterminate, (isIndeterminate) => {
  if (isIndeterminate && editPolicy.value) {
    editPolicy.value.endDate = null
  }
})

const fetchLinkedPolicies = async () => {
  isLoadingPolicies.value = true
  try {
    const currentScheduleId = props.scheduleId

    if (!currentScheduleId && currentScheduleId !== 0) {
      toast.error('ID do Agendamento não fornecido para buscar políticas.')
      draftItems.value = []
      originalItems.value = []
      isLoadingPolicies.value = false
      return
    }

    const response = await policyService.listSchedulePolicyLinks(currentScheduleId as number)
    draftItems.value = (response.data || []).map((item: SchedulePolicyLinkDetails) => {
      return {
        id: item.id,
        policyId: item.dsPaymentPolicy?.id,
        policyName: item.dsPaymentPolicy?.name,
        startDate: item.startDate,
        endDate: item.endDate,
        linkedAt: item.createdAt,
        linkedBy: item.linkedBy?.name ?? 'Usuário atual',
        status: 'vinculada' as const
      }
    })
    originalItems.value = JSON.parse(JSON.stringify(draftItems.value))
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível carregar as políticas vinculadas a esta escala.')
    draftItems.value = []
    originalItems.value = []
  } finally {
    isLoadingPolicies.value = false
  }
}

const handleRevertChanges = () => {
  draftItems.value = JSON.parse(JSON.stringify(originalItems.value))
  deletedItems.value = []
}

const handleCancelAddPolicy = () => {
  showAddPolicyForm.value = false
  selectedPolicy.value = null
  startDate.value = ''
  endDate.value = ''
}

const formatDate = (date: string | Date, isStart = true): string => {
  if (typeof date === 'string') date = new Date(date)
  const adjusted = new Date(date)
  adjusted.setUTCHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, isStart ? 0 : 999)
  return adjusted.toISOString()
}

const validateDateRange = (
  start: string | Date,
  end: string | Date | null,
  isIndeterminate: boolean
): boolean => {
  const startDay = toDateOnly(start)
  if (props.scheduleEndDate && startDay > toDateOnly(props.scheduleEndDate)) {
    toast.error(POLICY_DATE_MESSAGES.startAfterSchedule)
    return false
  }
  if (isIndeterminate || !end) return true
  const endDay = toDateOnly(end)
  if (endDay < startDay) {
    toast.error(POLICY_DATE_MESSAGES.endBeforeStart)
    return false
  }
  if (props.scheduleEndDate && endDay > toDateOnly(props.scheduleEndDate)) {
    toast.error(POLICY_DATE_MESSAGES.endAfterSchedule)
    return false
  }
  return true
}

const handleAddPolicy = () => {
  if (!selectedPolicy.value) {
    toast.error('Selecione uma política antes de continuar.')
    return
  }

  if (!startDate.value || (!isEndDateIndeterminate.value && !endDate.value)) {
    toast.error('Preencha todos os campos para incluir a política.')
    return
  }

  if (!validateDateRange(startDate.value, endDate.value, isEndDateIndeterminate.value)) return

  const alreadyExists = draftItems.value.some(
    (item) => item.policyId === selectedPolicy.value!.id && item.status !== 'nao_vinculada'
  )

  if (alreadyExists) {
    toast.error('Esta política já está vinculada.')
    return
  }

  draftItems.value.push({
    policyId: selectedPolicy.value.id,
    policyName: selectedPolicy.value.name,
    startDate: formatDate(startDate.value, true),
    endDate: isEndDateIndeterminate.value
      ? null
      : formatDate(endDate.value as string | Date, false),
    linkedAt: new Date().toISOString(),
    linkedBy: selectedPolicy.value.createdBy?.name ?? 'Usuário atual',
    status: 'nao_vinculada'
  })

  handleCancelAddPolicy()
}

const handleEdit = (index: number) => {
  editItemIndex.value = index
  const policyToEdit = draftItems.value[index]
  editPolicy.value = { ...policyToEdit }

  isEditEndDateIndeterminate.value = policyToEdit.endDate === null

  showEditPolicyForm.value = true
}

const handleCancelEditPolicy = () => {
  showEditPolicyForm.value = false
  editItemIndex.value = null
  editPolicy.value = null
}

const handleConfirmEditPolicy = () => {
  if (!editPolicy.value) {
    toast.error('Algo deu errado. Dados da edição não encontrados.')
    return
  }

  if (
    !editPolicy.value.startDate ||
    (!isEditEndDateIndeterminate.value && !editPolicy.value.endDate)
  ) {
    toast.error('Preencha todos os campos obrigatórios.')
    return
  }

  if (
    !validateDateRange(
      editPolicy.value.startDate,
      editPolicy.value.endDate,
      isEditEndDateIndeterminate.value
    )
  )
    return

  editPolicy.value.startDate = formatDate(editPolicy.value.startDate, true)
  editPolicy.value.endDate = isEditEndDateIndeterminate.value
    ? null
    : formatDate(editPolicy.value.endDate as string | Date, false)

  if (editItemIndex.value !== null) {
    draftItems.value[editItemIndex.value] = {
      ...editPolicy.value,
      status: 'em_edicao'
    }
    handleCancelEditPolicy()
  }
}

const handleDeletePolicy = () => {
  if (editItemIndex.value !== null) {
    const removedItem = draftItems.value[editItemIndex.value]
    if (removedItem && typeof removedItem.id === 'number') {
      deletedItems.value.push(removedItem)
    }
    draftItems.value.splice(editItemIndex.value, 1)
    handleCancelEditPolicy()
  }
}

const formatToBR = (isoDate: string) => {
  if (!isoDate) return '-'

  const date = new Date(isoDate)
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${day}/${month}/${year}`
}

const policies = computed(() =>
  draftItems.value.map((item) => ({
    id: item.policyId,
    name: item.policyName,
    period: item.startDate
      ? item.endDate
        ? `${formatToBR(item.startDate)} à ${formatToBR(item.endDate)}`
        : `${formatToBR(item.startDate)} em diante`
      : '-',
    linkedAt: item.linkedAt ? formatToBR(item.linkedAt) : '-',
    responsible: item.linkedBy ?? '-',
    status: item.status
  }))
)

const hasChanges = computed(() => {
  const draftJson = JSON.stringify(draftItems.value)
  const originalJson = JSON.stringify(originalItems.value)
  return draftJson !== originalJson || deletedItems.value.length > 0
})

const hasUnsavedChanges = computed(
  () =>
    draftItems.value.some(
      (item) => item.status === 'em_edicao' || item.status === 'nao_vinculada'
    ) || deletedItems.value.length > 0
)

watch(hasUnsavedChanges, (val) => {
  emit('update:hasUnsavedChanges', val)
})

/**
 * Mensagem da trava do Pós-Plantão (RN-07 / RF-12).
 *
 * Reativa ao 400: a granularidade do backend é "a mudança de vigência alcança
 * data já coberta por plantão aprovado". Fechar a vigência aberta e criar uma
 * futura PASSA — sondar por escala travaria também esse caso, que é a operação
 * legítima.
 */
const postStationLockMessage = ref<string | null>(null)

const handleSave = async () => {
  emit('update:isSaving', true)
  isSavingPolicies.value = true
  postStationLockMessage.value = null

  const currentScheduleId = props.scheduleId
  if (!currentScheduleId && currentScheduleId !== 0) {
    toast.error('ID do Agendamento (Schedule) não encontrado. Não é possível salvar.')
    emit('update:isSaving', false)
    isSavingPolicies.value = false
    return
  }

  try {
    const editedOrNewItems: LinkPoliciesToScheduleRequestData = draftItems.value
      .filter(
        (item) =>
          item.status === 'em_edicao' ||
          item.status === 'nao_vinculada' ||
          item.status === 'vinculada'
      )
      .map((item) => {
        const startDate = new Date(item.startDate)
        startDate.setUTCHours(0, 0, 0, 0)

        const endDate = item.endDate ? new Date(item.endDate) : null
        if (endDate) {
          endDate.setUTCHours(23, 59, 59, 999)
        }

        const base = {
          policyId: item.policyId,
          startDate,
          endDate,
          isDeleted: false
        }
        if (typeof item.id === 'number' && Number.isFinite(item.id)) {
          ;(base as any).id = item.id
        }
        return base
      })

    const removedItems: LinkPoliciesToScheduleRequestData = deletedItems.value.map((item) => ({
      id: item.id!,
      policyId: item.policyId,
      startDate: new Date(item.startDate),
      endDate: item.endDate ? new Date(item.endDate) : null,
      isDeleted: true
    }))

    const payload: LinkPoliciesToScheduleRequestData = [...editedOrNewItems, ...removedItems]
    await policyService.linkPoliciesToSchedule(currentScheduleId, payload)

    toast.success('Políticas vinculadas foram salvas com sucesso!')

    await fetchLinkedPolicies()
  } catch (error: unknown) {
    if (isPostStationApprovedError(error)) {
      postStationLockMessage.value = getPostStationApprovedMessage(error)
      toast.error(postStationLockMessage.value)
      return
    }

    // O backend devolve 400 com mensagens pt-BR significativas (sobreposição
    // ou lacuna de vigência) — o `notifyError` as repassa e só usa o fallback
    // quando não há mensagem exibível.
    notifyError(error, 'Não foi possível salvar as políticas vinculadas.')
  } finally {
    emit('update:isSaving', false)
    isSavingPolicies.value = false
  }
}

__expose({
  handleSave
})

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_ds_policy_select = _resolveComponent("ds-policy-select")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isLoadingPolicies.value || isSavingPolicies.value)
      ? (_openBlock(), _createBlock(Spinner, {
          key: 0,
          color: "primary",
          size: "5rem",
          width: "0.5rem",
          class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
        }))
      : _createCommentVNode("", true),
    (_unref(flags).enabledDsAttendancePolicy)
      ? (_openBlock(), _createBlock(_component_v_autocomplete, {
          key: 1,
          items: attendancePolicies.value,
          "item-title": "name",
          "item-value": "id",
          loading: isLoadingAttendancePolicies.value,
          class: "w-[340px] mt-2",
          color: "primary20",
          label: "Modalidade selecionada",
          theme: "light",
          variant: "outlined",
          density: "comfortable"
        }, null, 8, ["items", "loading"]))
      : _createCommentVNode("", true),
    (postStationLockMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(SVGIcon, {
            path: _unref(mdiLock),
            class: "w-4 h-4 mt-0.5 shrink-0 text-primary30"
          }, null, 8, ["path"]),
          _createElementVNode("span", null, _toDisplayString(postStationLockMessage.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Legend),
      _createElementVNode("div", _hoisted_3, [
        (hasChanges.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "text-sm text-gray-400 hover:text-gray-700 font-semibold",
              onClick: handleRevertChanges
            }, " Reverter alterações "))
          : _createCommentVNode("", true),
        (canCreatePolicyLink.value)
          ? (_openBlock(), _createBlock(OutlineButton, {
              key: 1,
              text: "Adicionar política",
              height: "32px",
              "append-icon": _unref(mdiPlus),
              class: "text-sm !w-auto !px-4 !py-1",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddPolicyForm.value = !showAddPolicyForm.value))
            }, null, 8, ["append-icon"]))
          : _createCommentVNode("", true),
        (showAddPolicyForm.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", { class: "flex justify-between items-center mb-3" }, [
                _hoisted_5,
                _createElementVNode("button", {
                  class: "text-gray-400 hover:text-gray-700",
                  onClick: handleCancelAddPolicy
                }, " ✕ ")
              ]),
              _createVNode(DsPaymentPolicySelect, {
                modelValue: selectedPolicy.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedPolicy).value = $event)),
                "schedule-id": props.scheduleId,
                class: "w-full mb-4"
              }, null, 8, ["modelValue", "schedule-id"]),
              _createVNode(_unref(DatePicker), {
                modelValue: startDate.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((startDate).value = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "max-date": _unref(scheduleMaxDate),
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    "data-testid": "add-policy-start-date-input",
                    label: "Início da vigência",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue", "max-date"]),
              _createVNode(_unref(DatePicker), {
                modelValue: endDate.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((endDate).value = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                disabled: isEndDateIndeterminate.value,
                "max-date": _unref(scheduleMaxDate),
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    "data-testid": "add-policy-end-date-input",
                    label: "Final da vigência",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full mb-4",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar),
                    disabled: isEndDateIndeterminate.value,
                    "hide-details": ""
                  }), null, 16, ["model-value", "prepend-inner-icon", "disabled"])
                ]),
                _: 1
              }, 8, ["modelValue", "disabled", "max-date"]),
              _createVNode(DsSwitch, {
                modelValue: isEndDateIndeterminate.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isEndDateIndeterminate).value = $event)),
                label: "Fim de vigência indeterminado",
                size: "small",
                "data-testid": "add-policy-indeterminate-switch"
              }, null, 8, ["modelValue"]),
              _createVNode(FilledButton, {
                text: "Incluir política",
                width: "100%",
                onClick: handleAddPolicy
              })
            ]))
          : _createCommentVNode("", true),
        (showEditPolicyForm.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", { class: "flex justify-between items-center mb-3" }, [
                _hoisted_7,
                _createElementVNode("button", {
                  class: "text-gray-400 hover:text-gray-700",
                  onClick: handleCancelEditPolicy
                }, " ✕ ")
              ]),
              _createElementVNode("div", _hoisted_8, " Data de criação: " + _toDisplayString(formatToBR(editPolicy.value?.linkedAt || '')), 1),
              _createVNode(_component_ds_policy_select, {
                modelValue: editPolicy.value!.policyName,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editPolicy.value!.policyName) = $event)),
                "schedule-id": props.scheduleId,
                class: "w-full mb-4",
                disabled: ""
              }, null, 8, ["modelValue", "schedule-id"]),
              _createVNode(_unref(DatePicker), {
                modelValue: editPolicy.value!.startDate,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editPolicy.value!.startDate) = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "max-date": _unref(scheduleMaxDate),
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    "data-testid": "edit-policy-start-date-input",
                    label: "Início da vigência",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue", "max-date"]),
              _createVNode(_unref(DatePicker), {
                modelValue: editPolicy.value!.endDate,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editPolicy.value!.endDate) = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                disabled: isEditEndDateIndeterminate.value,
                "max-date": _unref(scheduleMaxDate),
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    "data-testid": "edit-policy-end-date-input",
                    label: "Final da vigência",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full mb-4",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar),
                    disabled: isEditEndDateIndeterminate.value,
                    "hide-details": ""
                  }), null, 16, ["model-value", "prepend-inner-icon", "disabled"])
                ]),
                _: 1
              }, 8, ["modelValue", "disabled", "max-date"]),
              _createVNode(DsSwitch, {
                modelValue: isEditEndDateIndeterminate.value,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((isEditEndDateIndeterminate).value = $event)),
                label: "Fim de vigência indeterminado",
                size: "small",
                "data-testid": "edit-policy-indeterminate-switch"
              }, null, 8, ["modelValue"]),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(FilledButton, {
                  text: "Concluir",
                  width: "50%",
                  onClick: handleConfirmEditPolicy
                }),
                _createVNode(FilledButton, {
                  disabled: !canDeactivatePolicyLink.value,
                  text: "Excluir vínculo",
                  width: "50%",
                  class: "!bg-transparent border-2 !border-red-500 !text-red-500",
                  onClick: handleDeletePolicy
                }, null, 8, ["disabled"])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (policies.value.length)
      ? (_openBlock(), _createBlock(TableModular, {
          key: 3,
          rows: policies.value,
          columns: [
      {
        key: 'name',
        label: 'Política de pagamento',
        cols: 'lg:col-span-3 md:col-span-4 col-span-12'
      },
      { key: 'period', label: 'Período', cols: 'lg:col-span-3 md:col-span-4 col-span-12' },
      {
        key: 'linkedAt',
        label: 'Data do vínculo',
        cols: 'lg:col-span-1 md:col-span-3 col-span-12'
      },
      {
        key: 'responsible',
        label: 'Responsável',
        cols: 'lg:col-span-2 md:col-span-4 col-span-12'
      }
    ]
        }, {
          actions: _withCtx(({ index }) => [
            (canEditPolicyLinks.value)
              ? (_openBlock(), _createBlock(EditIcon, {
                  key: 0,
                  color: "#2f7f91",
                  class: "cursor-pointer ml-4 w-5 h-5",
                  onClick: ($event: any) => (handleEdit(index))
                }, null, 8, ["onClick"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["rows"]))
      : (_openBlock(), _createBlock(EmptyList, {
          key: 4,
          title: "Nenhuma política vinculada encontrada",
          class: "mt-8"
        }))
  ], 64))
}
}

})